import { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
	Sparkles,
	MessageCircle,
	Send,
	Filter,
	MessageCircleMore,
	ChevronRight,
	Circle,
} from 'lucide-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import UserAvatar from '@CommonRepresentationalDisplay/UserAvatar';
import WhatsappChatsViewService from '@Helpers/URLHelpers/WeboutService/Views/WhatsappChatsViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsappChatsList.scss';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 10;

export default function WhatsappChatsList() {

	const navigate = useNavigate();
	const [chatsList, setChatsList] = useState([]);
	const [listLoading, setListLoading] = useState(false);
	const [listPage, setListPage] = useState(0);
	const [listHasMore, setListHasMore] = useState(true);
	const [showUnread, setShowUnread] = useState(false);
	const [showOpen, setShowOpen] = useState(false);
	const [expandedRows, setExpandedRows] = useState(null);
	const chatsListRef = useRef(null);
	const { activeUserId } = useReduxActiveUser();
	const { currentProjectId } = useReduxProject();
	const whatsappChatsViewService = new WhatsappChatsViewService();
	const urlEncryptionService = new UrlEncryptionService();
	const errorHandler = new ErrorHandler();

	const buildListFilter = useCallback(
		(page) => ({
			whatsappChatsFilter: {
				projectIdList: [currentProjectId],
				...(showUnread
					? {
							sortBy: 'unreadCount',
							sortAsc: false,
							unreadCount: 1,
						}
					: {}),
				...(showOpen
					? {
							lastReceivedMessageTimeStampFrom: Date.now() - ONE_DAY_MS,
						}
					: {}),
			},
			page,
			size: PAGE_SIZE,
		}),
		[currentProjectId, showUnread, showOpen],
	);

	useEffect(() => {
		if (currentProjectId) {
			resetAndLoadContacts();
		}
	}, [currentProjectId, showUnread, showOpen]);

	const resetAndLoadContacts = async () => {
		setListLoading(true);
		setChatsList([]);
		setListPage(0);
		setListHasMore(true);
		try {
			const response = await whatsappChatsViewService.getListFiltered(
				buildListFilter(0),
			);
			if (response.status < 400) {
				const data = JSON.parse(response.body);
				setChatsList(data.conversationList ?? []);
				setListHasMore((data.totalPages ?? 0) > 1);
				setListPage(1);
			}
		} catch (err) {
			errorHandler.handleError(err);
		} finally {
			setListLoading(false);
		}
	};

	const handleWhatsappPage = () => {
		navigate('/home/whatsapp');
	};

	const handleChatView = (item) => {
		const id = urlEncryptionService.encryptData(item?.nameCardId);
		navigate('/home/whatsapp/?contact=' + id);
	};

	const getItemName = (row) => {
		const chat = row;
		const hasUnread = Number(chat?.unreadCount) > 0;
		return (
			<div
				className={`wa-conversation ${
					hasUnread ? 'wa-conversation--unread' : ''
				}`}
				onClick={() => handleChatView(chat)}
			>
				<div className='wa-conversation__avatar'>
					<UserAvatar name={chat?.userName} size='small' />
					{hasUnread && <span className='wa-conversation__online' />}
				</div>
				<div className='wa-conversation__content'>
					<div className='wa-conversation__top'>
						<p className='wa-conversation__name'>
							{chat?.userName || 'Unknown contact'}
						</p>
					</div>
					<p className='wa-conversation__message'>
						{chat?.lastMessage || 'No messages yet'}
					</p>
				</div>
			</div>
		);
	};

	const getTime = (row) => {
		const chat = row;
		const isOpen =
			Date.now() - Number(chat?.lastReceivedMessageTimeStamp) <= ONE_DAY_MS;
		const hasUnread = Number(chat?.unreadCount) > 0;
		return (
			<div className='wa-conversation-meta'>
				<span className='wa-conversation-meta__time'>
					{chat?.lastMessageTimeStamp
						? moment(new Date(chat.lastMessageTimeStamp)).fromNow()
						: '-'}
				</span>
				<div className='wa-conversation-meta__badges'>
					{isOpen && (
						<span className='wa-open'>
							<span className='wa-open__dot' />
							Open
						</span>
					)}
					{hasUnread && (
						<span className='wa-unread'>
							{chat.unreadCount > 99 ? '99+' : chat.unreadCount}
						</span>
					)}
				</div>
			</div>
		);
	};

	const emptyList = () => (
		<div className='wa-empty'>
			<div className='wa-empty__icon'>
				<MessageCircleMore size={20} />
			</div>
			<h4>
				{showUnread || showOpen ? 'No matching chats' : 'No conversations yet'}
			</h4>
			<p>
				{showUnread || showOpen
					? 'Try changing your filters.'
					: 'Your WhatsApp conversations will appear here.'}
			</p>
			{(showUnread || showOpen) && (
				<button
					onClick={() => {
						setShowUnread(false);
						setShowOpen(false);
					}}
				>
					Clear filters
				</button>
			)}
		</div>
	);

	const footer = (
		<div className='wa-footer'>
			<div className='wa-footer__info'>
				<span className='wa-footer__dot' />
				<span>
					{chatsList.length
						? `${chatsList.length} recent conversations`
						: 'No conversations'}
				</span>
			</div>
			<button className='wa-footer__button' onClick={handleWhatsappPage}>
				<Send size={12} />
				Send message
			</button>
		</div>
	);

	return (
		<div className='whatsapp-chats'>
			<div className='whatsapp-chats__header'>
				<div className='whatsapp-chats__heading'>
					<div className='whatsapp-chats__icon'>
						<MessageCircle size={15} />
					</div>
					<div>
						<div className='whatsapp-chats__title'>WhatsApp chats</div>
						<div className='whatsapp-chats__subtitle'>Recent conversations</div>
					</div>
				</div>
				<button
					className='whatsapp-chats__view-all'
					onClick={handleWhatsappPage}
				>
					View all
					<ChevronRight size={13} />
				</button>
			</div>
			<div className='whatsapp-chats__filters'>
				<div className='whatsapp-chats__filter-label'>
					<Filter size={11} />
					<span>Filter</span>
				</div>
				<button
					className={`wa-filter ${showUnread ? 'wa-filter--active' : ''}`}
					onClick={() => setShowUnread((value) => !value)}
				>
					<span className='wa-filter__dot wa-filter__dot--unread' />
					Unread
					{showUnread && <span className='wa-filter__check'>✓</span>}
				</button>
				<button
					className={`wa-filter ${showOpen ? 'wa-filter--active' : ''}`}
					onClick={() => setShowOpen((value) => !value)}
				>
					<span className='wa-filter__dot wa-filter__dot--open' />
					Open
					{showOpen && <span className='wa-filter__check'>✓</span>}
				</button>
			</div>
			<div className='whatsapp-chats__table'>
				<DataTable
					value={chatsList}
					ref={chatsListRef}
					loading={listLoading}
					scrollable
					scrollHeight='235px'
					footer={footer}
					emptyMessage={emptyList}
					dataKey='id'
					expandedRows={expandedRows}
					onRowToggle={(e) => setExpandedRows(e.data)}
				>
					<Column
						field='name'
						header='Conversation'
						body={getItemName}
						style={{
							minWidth: '260px',
							width: '68%',
						}}
					/>
					<Column
						field='time'
						header=''
						body={getTime}
						style={{
							minWidth: '125px',
							width: '32%',
						}}
					/>
				</DataTable>
			</div>
		</div>
	);
}